import { NATS_KV_BUCKET_NAME_PATTERN, PLATFORM_STREAM_NAME_PATTERN, natsKvBucketOf, platformStreamOf } from "./platform-streams.js";
import { agentStreamName, agentStreamSubjects, assertValidStreamOrgId, assertValidStreamPrincipalId, dualJsApi, isAgentStreamKind, jsApi, jsApiPlain } from "./event-subjects.js";
//#region src/stream-verbs.ts
/**
* The four NATS grant resource types, their verbs, and the one compiler that turns a verb on a
* resource into NATS permissions (2026-10-05-stream-resource-permissions, overview section 5a,
* D-10 to D-24; the stream families of round 1, D-2 to D-6, carry over where the table says so).
*
* | type | resource id | verbs |
* |---|---|---|
* | `nats_stream` | a principal slug (`S_<org>_<slug>`) or a platform stream name (`ZION_…`) | `manage`, `append`, `read` |
* | `nats_inbox` | a principal slug: the inbox facet `org.<org>.p.<kind>.<slug>.inbox.>` | `manage`, `post`, `receive` |
* | `nats_request_endpoint` | `team/<slug>`: the team request subjects `org.<org>.rq.team.<slug>.>` (R-042) | `manage`, `call`, `serve` |
* | `nats_kv_bucket` | a bucket name of `NATS_KV_BUCKETS`: the stream `KV_<bucket>` | `manage`, `read`, `write` |
*
* A grant row `<type>:<verb>` on a resource, and every standing grant of `nats-standing-grants.ts`,
* compile through {@link compileNatsGrantSubjects}. So one table decides which subjects a verb
* reaches, and the credential compile of core-api, the standing grants and the helpers of
* `event-subjects.ts` cannot drift apart.
*
* The subject families come from `research/jetstream-op-subjects-measured.md` and
* `research/r2-endpoint-inbox-mechanics.md` (nats-server v2.14.0 and v2.10.22, nats.js 2.29.3), and
* from the helpers of `event-subjects.ts`, whose output they reproduce byte for byte. No verb
* compiles to stream create, update, delete, purge, snapshot, restore, peer remove or leader
* stepdown, or to a consumer delete or pause: those stay with the operator program.
*
* Load order. `event-subjects.ts` imports `nats-standing-grants.ts`, which imports this module, which
* imports `event-subjects.ts`. No module of that cycle reads a binding of another one while it loads;
* each one reads them inside function bodies only. Keep it so: a constant here built from an
* `event-subjects.ts` export throws a `ReferenceError` for the first importer of `event-subjects.ts`.
* For the same reason the `manage` literal here is not `RESOURCE_GRANT_MANAGE_VERB`
* (`resource-grants.ts` reads this module at load); `stream-verbs.test.ts` pins the two equal.
*
* Every export is pure: no clock, no I/O, no module state beyond frozen constants.
*/
/**
* The four NATS grant resource types (overview D-10), in catalog order. The type name carries the
* domain, and each type has its own verb list. Frozen.
*/
const NATS_GRANT_RESOURCE_TYPES = Object.freeze([
	"nats_stream",
	"nats_inbox",
	"nats_request_endpoint",
	"nats_kv_bucket"
]);
/**
* The verbs of `nats_stream` (overview D-11), in the order of its `RESOURCE_TYPE_CATALOG` row,
* `manage` first.
*
* | verb | what its holder does | subjects |
* |---|---|---|
* | `manage` | writes grants of `read` on this stream | none (ruling R-006 of 2026-09-27-dp-resource-grants) |
* | `append` | publishes into the stream: author only, from the owner's identity; no grant row carries it (R-022) | `org.<org>.p.<kind>.<slug>.>` |
* | `read` | consumer create, consumer info, fetch, ack and the stream info an ordered consumer needs | the 13 dual subjects of the own-stream grant of today |
*
* Frozen.
*/
const NATS_STREAM_VERBS = Object.freeze([
	"manage",
	"append",
	"read"
]);
/**
* The verbs of `nats_inbox` (overview D-12, D-21), `manage` first.
*
* | verb | what its holder does | subjects |
* |---|---|---|
* | `manage` | writes grants of `post` on this inbox (`receive` is reserved, R-046) | none |
* | `post` | publishes into the owner's inbox facet | `org.<org>.p.<kind>.<slug>.inbox.>` |
* | `receive` | the owner reads its inbox through its own stream; reserved for every grant (R-046) | none |
*
* Frozen.
*/
const NATS_INBOX_VERBS = Object.freeze([
	"manage",
	"post",
	"receive"
]);
/**
* The verbs of `nats_request_endpoint` (overview D-13, D-20), `manage` first.
*
* | verb | what its holder does | subjects |
* |---|---|---|
* | `manage` | writes grants of `call` and `serve` on this endpoint | none |
* | `call` | sends a request whose reply subject sits under the caller's scoped inbox `_INBOX.<principalId>.>`, which the caller sets as nats.js `inboxPrefix` | publish `org.<org>.rq.team.<slug>.>` |
* | `serve` | answers requests in the queue `rq-<slug>`, and publishes a reply only to an `_INBOX.…` subject | subscribe `org.<org>.rq.team.<slug>.> rq-<slug>`, publish `_INBOX.>` |
*
* The request subjects have their own namespace `org.<org>.rq.…`, outside the principal subtree
* `org.<org>.p.…` (ruling R-042). So the open inbox post `org.<org>.p.*.*.inbox.>`
* ({@link compileVerbOnEveryInbox}, `agentPublishSubjects`) and the own-subtree publish
* `org.<org>.p.<kind>.<id>.>` match no request subject, and a principal credential sends a request
* only through a `call` row. One code-defined glob still matches them: the org transport `org.<org>.>`
* of a unit-shaped host (`orgTransportGrantSubjects`), in publish and in subscribe. Such a host
* reaches every endpoint of its org with no `call` or `serve` row (overview section 9, R-042).
*
* The `serve` reply rides the publish `_INBOX.>`, and never the NATS response permission
* (`allow_responses`, `nats.resp`). The cause, as a chain:
*
* 1. NATS checks no permission on the reply subject that a publisher sets on a request.
* 2. A response permission lets the responder publish one message to the reply subject of each
*    request it received, outside its publish allow list.
* 3. So a `call` holder that sets the reply subject `org.<org>.p.agent.<victim>.events.x` or
*    `$JS.API.…` makes the `serve` holder publish there. The message lands in the victim's stream,
*    past the author-only rule of `append` (R-022), or reaches the JetStream API with the authority
*    of the `serve` holder (finding WS-6-R7, measured on nats-server 2.14.0 and 2.10.22).
*
* With the publish `_INBOX.>` in place of the response permission, the broker refuses each forged
* reply subject, and the reply to a scoped inbox passes. `_INBOX.>` is the responder publish that
* core-api's validators already admit, in publish only (`deriveDevNatsPermissions`, D1). Residual: a
* `serve` holder may publish more than one reply, and may publish to an `_INBOX` subject it did not
* receive. A scoped reply subject carries the random tokens of nats.js, which the holder learns only
* from a request.
*
* Frozen.
*/
const NATS_REQUEST_ENDPOINT_VERBS = Object.freeze([
	"manage",
	"call",
	"serve"
]);
/**
* The verbs of `nats_kv_bucket` (overview D-14), `manage` first.
*
* | verb | what its holder does | subjects |
* |---|---|---|
* | `manage` | writes grants of the bucket's org-grantable verbs | none |
* | `read` | stream-level for a standing grant; key-confined get alone on an org-keyed bucket (R-050) | see {@link compileNatsGrantSubjects} |
* | `write` | put, create, update, delete and key purge | `$KV.<bucket>.…` |
*
* Frozen.
*/
const NATS_KV_BUCKET_VERBS = Object.freeze([
	"manage",
	"read",
	"write"
]);
/**
* The verb list of each NATS grant resource type. `RESOURCE_TYPE_CATALOG` reads its four rows from
* here. The record has a null prototype and is frozen, so a read with an untrusted key such as
* `constructor` answers `undefined`.
*/
const NATS_GRANT_VERBS = Object.freeze(Object.assign(Object.create(null), {
	nats_stream: NATS_STREAM_VERBS,
	nats_inbox: NATS_INBOX_VERBS,
	nats_request_endpoint: NATS_REQUEST_ENDPOINT_VERBS,
	nats_kv_bucket: NATS_KV_BUCKET_VERBS
}));
/**
* The retired `nats_stream` verbs that the compile still reads, each mapped to the verb it compiles
* as (overview D-19, the expand phase).
*
* `consume` compiles as `read`: a roster or admin row written before the four-type split keeps its
* subjects until the data migration of the next release converts it to `read`. The catalog no longer
* lists `consume`, so the grant route refuses a new `consume` row with `verb_not_declared`. The
* retired `publish`, `write` and `operate` have no alias and compile to nothing.
*
* Readers. {@link compileNatsGrantSubjects} applies the alias. A reader of `RESOURCE_TYPE_CATALOG`
* sees no alias, so every reader that decides what a `nats_stream` row means must apply this record
* itself, or it disagrees with the compile through the expand phase. Ruling R-040 names the core-api
* readers that apply it: the permission check `checkResourceGrantPermission`, the effective read
* `listGrantedResourceIds` and the compile read `grantedResourceIdsWithin`. With the alias, a
* `consume` row counts as `read` in all three. The grant route still refuses a new `consume` or
* `publish` row with `verb_not_declared`. The record has a null prototype and is frozen.
*/
const NATS_STREAM_VERB_ALIASES = Object.freeze(Object.assign(Object.create(null), { consume: "read" }));
/**
* The JetStream API dialects a compile can emit.
*
* - `plain`: `$JS.API.<suffix>`, for a client that connects straight to the hub (a svc fixture, a
*   compute unit, a per-execution-context agent connection).
* - `qualified`: `$JS.hub.API.<suffix>`, for a client behind the leaf node, whose nats.js carries the
*   `apiPrefix`. On a KV bucket it also emits `$JS.hub.API.$KV.…` beside each `$KV.…` data subject,
*   because nats.js prefixes the data subject when an `apiPrefix` is set.
* - `dual`: both, for a credential whose connection form the platform does not know.
*
* `$JS.ACK.…` and the core NATS subjects of `append`, `post`, `call` and `serve` have one form in
* every dialect. Frozen.
*/
const STREAM_VERB_DIALECTS = Object.freeze([
	"plain",
	"qualified",
	"dual"
]);
/**
* The confinements of a compile: how far inside the resource the compiled subjects reach.
*
* - `org-grant`: the confinement of a grant row that an org member wrote. On a principal stream, an
*   inbox or an endpoint it is the resource, which carries the org in its subjects. `append`
*   compiles to nothing under it. On a platform stream or a KV bucket it admits only a verb in the
*   registry row's `orgGrantableVerbs`, only on the registry row itself (the object `platformStreamOf`
*   or `natsKvBucketOf` answers), and on a bucket only with the `org-prefixed` key layout, where it
*   confines every subject to the keys under `<orgId>.` (overview D-14).
* - `org`: a standing grant confined to the keys under `<orgId>.` of an `org-prefixed` bucket.
* - `principal`: a standing grant confined to the one key `<orgId>.<principalId>` of an
*   `org-prefixed` bucket.
* - `stream`: a standing grant on the whole stream or bucket.
*
* Only core-api's credential compile of a grant row passes `org-grant`. The other three are for the
* standing grants of `nats-standing-grants.ts`, which the platform defines in code. On a principal
* stream, an inbox and an endpoint every confinement compiles the same family, except `append`.
* Frozen.
*/
const STREAM_VERB_CONFINEMENTS = Object.freeze([
	"org-grant",
	"org",
	"principal",
	"stream"
]);
/**
* The grammar of a `nats_request_endpoint` resource id: `team/` and a team slug of 1 to 40 of
* `a-z`, `0-9` and `-` (overview D-20). The slug grammar is the stream principal token grammar
* (`STREAM_PRINCIPAL_ID_TOKEN_PATTERN`), because the slug becomes a subject token and the queue
* name `rq-<slug>`. A group whose slug falls outside it has no endpoint.
*/
const NATS_REQUEST_ENDPOINT_ID_PATTERN = /^team\/[a-z0-9-]{1,40}$/;
/** The prefix of every request endpoint id. */
const TEAM_ENDPOINT_PREFIX = "team/";
/**
* The resource id `team/<teamSlug>` of a team's request endpoint.
*
* Callers: core-api and the SDK, which name an endpoint for a group. Throws `TypeError` when the
* result fails {@link NATS_REQUEST_ENDPOINT_ID_PATTERN}, so no id the parser refuses is ever built.
*/
function requestEndpointId(teamSlug) {
	const id = `${TEAM_ENDPOINT_PREFIX}${String(teamSlug)}`;
	if (!NATS_REQUEST_ENDPOINT_ID_PATTERN.test(id)) throw new TypeError(`requestEndpointId: ${JSON.stringify(teamSlug)} is not a team slug`);
	return id;
}
/**
* The team slug of a request endpoint id, or `null` when `id` fails
* {@link NATS_REQUEST_ENDPOINT_ID_PATTERN} or is not a string.
*
* Caller: core-api's `ResourceLocator`, which looks the slug up as a `groups` row of the caller's
* org. Never throws.
*/
function requestEndpointSlugOf(id) {
	if (typeof id !== "string" || !NATS_REQUEST_ENDPOINT_ID_PATTERN.test(id)) return null;
	return id.slice(5);
}
/**
* The literal name of the one consumer a `nats_inbox:receive` grantee may create on the inbox
* owner's stream: `inbox-<granteeAgentId>` (overview D-21).
*
* A literal, platform-assigned name keeps the grantee off every other consumer of the stream, the
* owner's included; a wildcard name or an unnamed or ordered consumer could escape the facet filter
* (measured, `research/r2-endpoint-inbox-mechanics.md` section 3). Callers: the compile below; the
* SDK client, which must create the consumer under this name. Throws the token error of
* `assertValidStreamPrincipalId` when `granteeAgentId` fails `^[a-z0-9-]{1,40}$`.
*/
function inboxReceiveConsumerName(granteeAgentId) {
	assertValidStreamPrincipalId(granteeAgentId);
	return `inbox-${granteeAgentId}`;
}
const EMPTY = Object.freeze([]);
/** The forms of one API suffix in one dialect; `dual` is qualified first, then plain. */
function apiForms(suffix, dialect) {
	if (dialect === "plain") return [jsApiPlain(suffix)];
	if (dialect === "qualified") return [jsApi(suffix)];
	return dualJsApi(suffix);
}
/**
* Renders a family part by part: each part's dialect forms sit together (`dualJsApi` order). This is
* the order of every KV helper of today.
*/
function renderInterleaved(parts, dialect) {
	const out = [];
	for (const part of parts) if ("api" in part) out.push(...apiForms(part.api, dialect));
	else if ("data" in part) {
		out.push(part.data);
		if (dialect !== "plain") out.push(jsApi(part.data));
	} else out.push(part.raw);
	return out;
}
/**
* Renders a consumer family: in `dual`, the whole qualified family, then the plain API subjects,
* with `$JS.ACK` once. This is the order of `appSiblingStreamConsumerGrantSubjects` and of the agent
* own-stream grant of today.
*/
function renderBlocks(parts, dialect) {
	if (dialect !== "dual") return renderInterleaved(parts, dialect);
	return [...renderInterleaved(parts, "qualified"), ...renderInterleaved(parts.filter((part) => "api" in part), "plain")];
}
/**
* The `nats_stream:read` family on stream `s`. The bare `CONSUMER.CREATE.<s>` comes first: a
* filterless or unnamed create publishes to it, and the `.>` form cannot match it (incident
* 2026-07-03). The `STREAM.INFO.<s>` comes last: an ordered consumer resolves the stream before it
* creates the consumer. `DURABLE.CREATE` has no bare form in the protocol.
*/
function streamReadParts(s) {
	return [
		{ api: `CONSUMER.CREATE.${s}` },
		{ api: `CONSUMER.CREATE.${s}.>` },
		{ api: `CONSUMER.DURABLE.CREATE.${s}.>` },
		{ api: `CONSUMER.INFO.${s}.>` },
		{ api: `CONSUMER.MSG.NEXT.${s}.>` },
		{ raw: `$JS.ACK.${s}.>` },
		{ api: `STREAM.INFO.${s}` }
	];
}
/**
* The `nats_inbox:receive` family on stream `s`: the one consumer `n`, filtered in the create subject
* to the facet `f`. The broker refuses a create whose body filter differs from the subject suffix
* (err_code 10131) or carries several filters (err_code 10137), so the grantee cannot widen it.
*/
function inboxReceiveParts(s, n, f) {
	return [
		{ api: `CONSUMER.CREATE.${s}.${n}.${f}` },
		{ api: `CONSUMER.INFO.${s}.${n}` },
		{ api: `CONSUMER.MSG.NEXT.${s}.${n}` },
		{ raw: `$JS.ACK.${s}.${n}.>` }
	];
}
/**
* The stream-level read on KV stream `s`: bind and get. It carries no `$JS.API.INFO` and no consumer
* create, as the KV helpers of today do; the account probe rides a standing row of its own, and a
* svc's keys and watch ride its all-streams read.
*/
function kvStreamReadParts(s) {
	return [
		{ api: `STREAM.INFO.${s}` },
		{ api: `DIRECT.GET.${s}` },
		{ api: `DIRECT.GET.${s}.>` },
		{ api: `STREAM.MSG.GET.${s}` }
	];
}
/**
* The key-confined read on KV stream `s` of bucket `b` under key subtree `k`: the account probe, the
* subject-form direct get. It leaves out every request whose key or filter rides the request body,
* where no subject permission confines it, and every consumer create (R-050):
*
* - the body-form direct get, `STREAM.MSG.GET` and every consumer create, filtered or bare, because a
*   consumer create lets its holder set a push `deliver_subject` the broker never checks;
* - `STREAM.INFO.<s>`, whose body field `subjects_filter` makes the reply list every matching key
*   with its count. On a shared org-prefixed bucket that names the org id and the principal id of
*   every key of every org (R-007).
*
* So the read serves get alone: no watch, keys or history. A holder binds with
* `views.kv(b, { bindOnly: true, allow_direct: true })`, which publishes no `STREAM.INFO`.
*/
function kvKeyedReadParts(s, b, k) {
	return [{ api: "INFO" }, { api: `DIRECT.GET.${s}.$KV.${b}.${k}` }];
}
/** True when `value` is one of `list`, for an untrusted value. */
function isOneOf(list, value) {
	return list.some((member) => member === value);
}
/** The inbox facet `org.<org>.p.<kind>.<slug>.inbox.>` of a principal, or `null` with no valid kind. */
function inboxFacet(orgId, slug, streamKind) {
	if (streamKind === void 0 || !isAgentStreamKind(streamKind)) return null;
	assertValidStreamOrgId(orgId);
	assertValidStreamPrincipalId(slug);
	return `org.${orgId}.p.${streamKind}.${slug}.inbox.>`;
}
/**
* True when no grant row may carry `verb` on resource `resourceId` of `type`, whoever writes it, the
* org `owner` included (overview D-4, D-11, D-14, D-23; ruling R-022).
*
* | type | reserved verbs |
* |---|---|
* | `nats_stream` | `append` on every id (author only); every verb on a registered platform stream outside its `orgGrantableVerbs` (all three today) |
* | `nats_kv_bucket` | every verb on a registered bucket outside its `orgGrantableVerbs` |
* | `nats_inbox` | `receive` on every inbox (the owner's, R-046) |
* | `nats_request_endpoint` | none |
*
* Callers: core-api's grant route, which answers 400 `verb_reserved` when this answers `true`, before
* the writer policy; the writer policy; the sdk-node fake. It answers `false` for a verb outside the
* type's catalog row (the route answers `verb_not_declared` for it), for an id that is not
* registered (the locator answers 404 `resource_not_found`), for a type outside
* {@link NATS_GRANT_RESOURCE_TYPES}, and for a non-string type or verb. Two verbs are reserved on any
* id, a non-string one included: `nats_stream:append` and `nats_inbox:receive` (R-046). Never throws.
*/
function isNatsGrantVerbReserved(type, resourceId, verb) {
	if (type === "nats_stream") {
		if (verb === "append") return true;
		const stream = platformStreamOf(resourceId);
		return stream !== void 0 && isOneOf(NATS_STREAM_VERBS, verb) && !isOneOf(stream.orgGrantableVerbs, verb);
	}
	if (type === "nats_inbox") return verb === "receive";
	if (type === "nats_kv_bucket") {
		const bucket = natsKvBucketOf(resourceId);
		return bucket !== void 0 && isOneOf(NATS_KV_BUCKET_VERBS, verb) && !isOneOf(bucket.orgGrantableVerbs, verb);
	}
	return false;
}
/**
* Compiles one verb of one NATS grant type on one resource into the NATS permissions it reaches.
*
* Callers: core-api's credential compile, once per grant row (`org-grant`); the standing grant
* compile of `nats-standing-grants.ts`; `appSiblingStreamConsumerGrantSubjects` in
* `event-subjects.ts`.
*
* The answer per type, target and verb (`s` is a stream name, `b` a bucket, `<org>` the `orgId`, and
* every API subject is rendered in the `dialect`). A list not named below is empty, and
* `allowResponses` is `null` unless named:
*
* - `nats_stream`, principal target, `s = S_<org>_<slug>`:
*   - `read`, and its alias `consume` ({@link NATS_STREAM_VERB_ALIASES}), any confinement:
*     `CONSUMER.CREATE.s`, `CONSUMER.CREATE.s.>`, `CONSUMER.DURABLE.CREATE.s.>`, `CONSUMER.INFO.s.>`,
*     `CONSUMER.MSG.NEXT.s.>`, `$JS.ACK.s.>`, `STREAM.INFO.s`. In `dual`, the qualified seven, then
*     the plain six with no second `$JS.ACK`: 13 subjects.
*   - `append`, confinement `org`, `principal` or `stream`, with a `streamKind`:
*     `org.<org>.p.<streamKind>.<slug>.>`. Under `org-grant`: nothing, because append is author only.
*   - `manage`: nothing.
* - `nats_stream`, platform-stream target: confinement `stream` only, `read` on the stream name as
*   above. Every other verb and confinement: nothing; under `org-grant` a verb outside the row's
*   `orgGrantableVerbs` (all of them today) compiles to nothing.
* - `nats_inbox`, principal target with a `streamKind`, any confinement, `f` the facet
*   `org.<org>.p.<streamKind>.<slug>.inbox.>`:
*   - `post`: publish `f`.
*   - `receive`, with `granteeId`, `n = inbox-<granteeId>`: `CONSUMER.CREATE.s.n.f`,
*     `CONSUMER.INFO.s.n`, `CONSUMER.MSG.NEXT.s.n`, `$JS.ACK.s.n.>`; in `dual` the qualified four,
*     then the plain three. With no `granteeId`: nothing.
*   - `manage`: nothing.
* - `nats_request_endpoint`, team target, any confinement, `e = org.<org>.rq.team.<slug>.>` (R-042):
*   - `call`: publish `e`. Of the code-defined grants only the org transport of a unit-shaped host
*     also matches `e`; see {@link NATS_REQUEST_ENDPOINT_VERBS}.
*   - `serve`: subscribe `e rq-<slug>` and publish `_INBOX.>`, with `allowResponses` `null`.
*   - `manage`: nothing.
* - `nats_kv_bucket`, bucket target, `s = KV_<b>`:
*   - confinement `stream`: `read` is `STREAM.INFO.s`, `DIRECT.GET.s`, `DIRECT.GET.s.>`,
*     `STREAM.MSG.GET.s`; `write` is `$KV.b.>`.
*   - confinement `org-grant`, `org` or `principal`, only on an `org-prefixed` bucket; key subtree
*     `k` is `<org>.>` (`org-grant`, `org`) or `<org>.<principalId>` (`principal`). `read` is
*     `INFO`, `DIRECT.GET.s.$KV.b.k`, with no `STREAM.INFO.s` (R-007) and no consumer create (R-050);
*     `write` is `INFO`, `$KV.b.k`. `org-grant` also needs the verb in the row's
*     `orgGrantableVerbs` and the target to be the registry row itself.
*   - `manage`: nothing.
*
* In `qualified` and `dual`, each `$KV.…` data subject is followed by its `$JS.hub.API.$KV.…` form.
*
* Errors. It throws the token error of `agentStreamName`, `assertValidStreamOrgId` or
* `assertValidStreamPrincipalId` when an org, slug, principal id or grantee id it emits fails the
* token grammar, as every helper of `event-subjects.ts` does, so no malformed id reaches a subject.
* Every other bad input compiles to nothing (defaults row F-3): an unknown type, verb, target type,
* confinement or dialect; a target of a kind the type does not take; a platform stream name outside
* `PLATFORM_STREAM_NAME_PATTERN`; a bucket name outside `NATS_KV_BUCKET_NAME_PATTERN`.
*
* Invariants: every publish subject, and the subject part of every subscribe entry, is
* whole-token-legal (`assertNatsGrantTokensLegal`); a queue-scoped subscribe entry is
* `<subject> rq-<slug>`, with exactly one space; `allowResponses` is `null`; no subject names
* stream create, update, delete, purge, snapshot, restore, peer remove or leader stepdown, or a
* consumer delete or pause; no subject is a bare `CONSUMER.CREATE` or a wildcard-named create on an
* inbox owner's stream for `receive`. Under `org-grant`, `org` and `principal` on a bucket, every
* subject but the account probe `INFO` carries the key subtree `k`, so no such compile reaches a key
* of another org. No side effects.
*/
function compileNatsGrantSubjects(type, target, verb, options) {
	if (!isOneOf(STREAM_VERB_CONFINEMENTS, options.confinement) || !isOneOf(STREAM_VERB_DIALECTS, options.dialect)) return answer([], []);
	switch (type) {
		case "nats_stream": return answer(streamSubjects(target, verb, options), []);
		case "nats_inbox": return answer(target.type === "principal" ? inboxSubjects(target, verb, options) : [], []);
		case "nats_request_endpoint": return target.type === "team" ? endpointPermissions(target.slug, verb, options.orgId) : answer([], []);
		case "nats_kv_bucket": return answer(target.type === "bucket" ? bucketSubjects(target.bucket, verb, options) : [], []);
		default: return answer([], []);
	}
}
/** One frozen answer. No verb fills the response permission (see {@link NATS_REQUEST_ENDPOINT_VERBS}). */
function answer(publish, subscribe) {
	return Object.freeze({
		publish: publish.length === 0 ? EMPTY : Object.freeze([...publish]),
		subscribe: subscribe.length === 0 ? EMPTY : Object.freeze([...subscribe]),
		allowResponses: null
	});
}
/** A `nats_stream` verb, after the alias of {@link NATS_STREAM_VERB_ALIASES}. */
function streamSubjects(target, verb, options) {
	const resolved = verb === "consume" ? NATS_STREAM_VERB_ALIASES.consume : verb;
	if (target.type === "principal") {
		if (resolved === "read") return renderBlocks(streamReadParts(agentStreamName(options.orgId, target.slug)), options.dialect);
		if (resolved !== "append" || options.confinement === "org-grant") return [];
		return target.streamKind !== void 0 && isAgentStreamKind(target.streamKind) ? agentStreamSubjects(options.orgId, target.streamKind, target.slug) : [];
	}
	if (target.type !== "platform-stream") return [];
	const stream = target.stream;
	if (!PLATFORM_STREAM_NAME_PATTERN.test(stream.name)) return [];
	if (options.confinement === "org-grant" && (platformStreamOf(stream.name) !== stream || !isOneOf(stream.orgGrantableVerbs, resolved))) return [];
	return resolved === "read" && options.confinement !== "org" && options.confinement !== "principal" ? renderBlocks(streamReadParts(stream.name), options.dialect) : [];
}
/** A `nats_inbox` verb on a principal's inbox facet. */
function inboxSubjects(target, verb, options) {
	if (verb !== "post" && verb !== "receive") return [];
	const facet = inboxFacet(options.orgId, target.slug, target.streamKind);
	if (facet === null) return [];
	if (verb === "post") return [facet];
	if (options.granteeId === void 0) return [];
	const consumer = inboxReceiveConsumerName(options.granteeId);
	return renderBlocks(inboxReceiveParts(agentStreamName(options.orgId, target.slug), consumer, facet), options.dialect);
}
/**
* The one publish of `serve`: every reply subject under `_INBOX`. It replaces the NATS response
* permission, which admits a reply subject that the caller forges (see {@link NATS_REQUEST_ENDPOINT_VERBS}).
*/
const SERVE_REPLY_PUBLISH = "_INBOX.>";
/** A `nats_request_endpoint` verb on the team `slug`. */
function endpointPermissions(slug, verb, orgId) {
	if (verb !== "call" && verb !== "serve") return answer([], []);
	assertValidStreamOrgId(orgId);
	assertValidStreamPrincipalId(slug);
	const subject = `org.${orgId}.rq.team.${slug}.>`;
	if (verb === "call") return answer([subject], []);
	return answer([SERVE_REPLY_PUBLISH], [`${subject} rq-${slug}`]);
}
/** A `nats_kv_bucket` verb on bucket `bucket`, under any confinement. */
function bucketSubjects(bucket, verb, options) {
	const b = bucket.name;
	if (!NATS_KV_BUCKET_NAME_PATTERN.test(b)) return [];
	const s = `KV_${b}`;
	if (options.confinement === "stream") {
		if (verb === "read") return renderInterleaved(kvStreamReadParts(s), options.dialect);
		return verb === "write" ? renderInterleaved([{ data: `$KV.${b}.>` }], options.dialect) : [];
	}
	if (bucket.keyLayout !== "org-prefixed") return [];
	if (options.confinement === "org-grant" && (natsKvBucketOf(b) !== bucket || !isOneOf(bucket.orgGrantableVerbs, verb))) return [];
	assertValidStreamOrgId(options.orgId);
	let k = `${options.orgId}.>`;
	if (options.confinement === "principal") {
		assertValidStreamPrincipalId(options.principalId);
		k = `${options.orgId}.${options.principalId}`;
	}
	if (verb === "read") return renderInterleaved(kvKeyedReadParts(s, b, k), options.dialect);
	return verb === "write" ? renderInterleaved([{ api: "INFO" }, { data: `$KV.${b}.${k}` }], options.dialect) : [];
}
/**
* The `nats_stream:read` family on every stream of the per-DP account: the standalone `*` stream
* token, which matches every single-token stream name. `$JS.API.CONSUMER.CREATE.*`, `….CREATE.*.>`,
* `….DURABLE.CREATE.*.>`, `….INFO.*.>`, `….MSG.NEXT.*.>`, `$JS.ACK.*.>`, `….STREAM.INFO.*` in
* `plain`. Any other verb, and an unknown dialect, compiles to nothing.
*
* Caller: the `svc` standing grant of `nats-standing-grants.ts`, alone. The package barrel does not
* export it, and no grant row can reach it: a grant row names one stream. The hard kind-gate that
* keeps it to a `service` principal lives in core-api (`principalTypeToStreamKind`).
*/
function compileVerbOnEveryStream(verb, dialect) {
	return verb === "read" && isOneOf(STREAM_VERB_DIALECTS, dialect) ? renderBlocks(streamReadParts("*"), dialect) : [];
}
/**
* The `nats_inbox:post` family on every inbox of the org `orgId`: `org.<orgId>.p.*.*.inbox.>`, the
* open post every principal of the org holds (overview D-12). It is for publish only: no subscribe
* list may carry it. The two `*` tokens match any kind and any id, so the glob matches every inbox
* facet of the org. It matches no team request subject `org.<orgId>.rq.team.<slug>.>` of
* `nats_request_endpoint`, whose second token is `rq` (R-042), so it sends no request without a
* `call` row. Any other verb compiles to nothing.
*
* Caller: the `org-inbox-post` standing grant of `nats-standing-grants.ts`, alone. The package barrel
* does not export it. Throws the token error of `assertValidStreamOrgId` for a malformed `orgId`.
*/
function compileVerbOnEveryInbox(verb, orgId) {
	if (verb !== "post") return [];
	assertValidStreamOrgId(orgId);
	return [`org.${orgId}.p.*.*.inbox.>`];
}
/**
* The account probe `$JS.API.INFO` in one dialect (`dual`: qualified, then plain). nats.js publishes
* it before a KV bind. Caller: the standing grant compile, for a row whose `accountInfo` is `true`.
* The package barrel does not export it.
*/
function accountInfoSubjects(dialect) {
	return apiForms("INFO", dialect);
}
//#endregion
export { NATS_GRANT_RESOURCE_TYPES, NATS_GRANT_VERBS, NATS_INBOX_VERBS, NATS_KV_BUCKET_VERBS, NATS_REQUEST_ENDPOINT_ID_PATTERN, NATS_REQUEST_ENDPOINT_VERBS, NATS_STREAM_VERBS, NATS_STREAM_VERB_ALIASES, STREAM_VERB_CONFINEMENTS, STREAM_VERB_DIALECTS, accountInfoSubjects, compileNatsGrantSubjects, compileVerbOnEveryInbox, compileVerbOnEveryStream, inboxReceiveConsumerName, isNatsGrantVerbReserved, requestEndpointId, requestEndpointSlugOf };

//# sourceMappingURL=stream-verbs.js.map